import { datadogLogs } from "@datadog/browser-logs";
import { useAuth } from "./API/auth";
import { isProd } from "./isProd";

const MAX_STACK_CHARS = 700;

/** Keeps log payloads small: events over 256KiB are dropped by the SDK. */
export const truncate = (text: string, max: number) =>
  text.length <= max ? text : `${text.slice(0, max)}… (${text.length} chars)`;

const isInternalUser = () =>
  useAuth.getState().username?.toLowerCase().endsWith("aureaimaging.com") ??
  false;

if (isProd) {
  datadogLogs.init({
    clientToken: "pub722e15662c55f4011a7df5395588df22",
    // `site` refers to the Datadog site parameter of your organization
    // see https://docs.datadoghq.com/getting_started/site/
    site: "datadoghq.eu",
    service: "customer-frontend",
    env: "prod",
    // A log is only sent for a tracked session, so this has to stay at 100:
    // anything lower drops the errors along with the session.
    sessionSampleRate: 100,
    // No identifier that follows a user across sessions.
    trackAnonymousUser: false,
    // Uncaught exceptions and unhandled rejections. Also turns on network error
    // logs, which `beforeSend` drops again.
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error"],
    // Internal users are excluded by withholding consent: nothing is collected
    // or sent while it is withheld. Held back until the auth state below
    // decides, so nothing is collected before that.
    trackingConsent: "not-granted",
    beforeSend: (event) => {
      // Network error logs put the response body in `error.stack`, and it can
      // carry customer data. Every failed request is reported by
      // `reportApiError` anyway, so nothing is lost by dropping these.
      if (event.origin === "network") {
        return false;
      }
      if (event.error) {
        // Every ApiError is reported through `reportApiError` as it is created,
        // so any other sighting of one is a duplicate: React, React Router and
        // our error boundary each console.error it on its way up, and loaders
        // that stream a promise to <Await> also let it reach onunhandledrejection.
        if (event.error.kind === "ApiError" && event.origin !== "logger") {
          return false;
        }
        if (event.error.stack) {
          event.error.stack = truncate(event.error.stack, MAX_STACK_CHARS);
        }
      }
      return true;
    },
  });

  let lastConsent: "granted" | "not-granted" | undefined;

  /**
   * Nothing is collected or sent while consent is withheld, which is how
   * internal users are kept out of Datadog entirely.
   */
  const syncTrackingConsent = () => {
    const consent = isInternalUser() ? "not-granted" : "granted";
    if (consent === lastConsent) {
      return;
    }
    lastConsent = consent;

    datadogLogs.setTrackingConsent(consent);
  };

  const syncLogsUser = () => {
    const { accessTokenPayload } = useAuth.getState();
    const id = accessTokenPayload?.sub ?? accessTokenPayload?.id;

    if (!id) {
      datadogLogs.clearUser();
      datadogLogs.clearAccount();
      return;
    }

    datadogLogs.setUser({ id });
  };

  const onAuthChange = () => {
    syncTrackingConsent();
    syncLogsUser();
  };
  // Runs immediately so consent is decided before anything can be collected, then
  // keeps it in sync as the user logs in/out.
  onAuthChange();
  useAuth.subscribe(onAuthChange);
}

/**
 * Tag subsequent logs with the customer the user is working on, as
 * `@account.id` / `@account.name`.
 *
 * Not retroactive: errors already sent in this session (login, customer
 * select) have no account set.
 */
export const setLogsCustomer = (customer: { id: number; name: string }) => {
  if (!isProd) {
    return;
  }

  datadogLogs.setAccount({ id: String(customer.id), name: customer.name });
};
